import type { EditControlProps } from '../../declare'
import { useState, useMemo, useRef } from 'react'
import { Upload, Button } from 'antd'
import { UploadOutlined } from '@ant-design/icons'
import { postMsg } from 'src/components'
import { appStore } from 'src/stores'
import { getImgBase, getApiURL } from 'src/utils/axios'
import { uniqWith } from 'lodash'

type FileItem = {
  FileId: string,
  FilePath: string,
  FileName: string
}

const FileUpload = (props: EditControlProps) => {
  const [loading, setLoading] = useState(false)

  const files = useMemo<FileItem[]>(() => {
    let list = []
    try { list = JSON.parse(props.value) } catch (err) { }
    return list
  }, [props.value])

  const handleChange = (uploadedFiles: string) => {
    props.onChange?.(uploadedFiles)
  }

  const handleUpload = (fileList: File[]) => {
    const { Tenant = null, OrgId } = appStore.current.headers
    const { INPUT_DATA_API: url, FILE_SIZE: fileSize } = props.config

    const invalidFiles: File[] = []
    const file = new FormData()
    fileList.forEach(item => {
      if (fileSize && item.size / 1024 / 1024 > fileSize) {
        invalidFiles.push(item)
      } else {
        file.append('file', item)
      }
    })

    if (invalidFiles.length) {
      const errMsg = invalidFiles.slice(0, 3).map(item => item.name).join('，')
      postMsg(`${errMsg}等${invalidFiles.length}个上传文件超过${fileSize}MB，请重新选择`, 'error')
    }

    if (invalidFiles.length === fileList.length) return

    setLoading(true)
    fetch(getApiURL(url), {
      method: 'POST',
      headers: { Tenant: Tenant ?? null, OrgId },
      body: file
    }).then(res => res.json())
      .then(res => {
        if (res && res.IsSuccessful) {
          postMsg('上传成功')
          // 存在已上传的文件，所以这边做 concat 处理
          const uploadedFiles = uniqWith(res.Data.concat(files), function (a: FileItem, b: FileItem) { return a.FileName === b.FileName })
          handleChange(JSON.stringify(uploadedFiles))
        } else {
          postMsg('上传失败', 'error')
        }
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const timer = useRef<any>(-1)
  const beforeUpload = (File: any, fileList: any[]) => {
    // 多选上传时，这个方法会重复调用，因此定时以节流
    clearTimeout(timer.current)
    timer.current = setTimeout(() => handleUpload(fileList), 300)
    return false
  }

  const handleRemove = (file: any) => {
    const findIndex = files.findIndex(item => item.FileName === file.name)
    if (findIndex !== -1) {
      const uploadedFiles = files.filter(item => item.FileName !== file.name)
      handleChange(uploadedFiles.length ? JSON.stringify(uploadedFiles) : '')
    }
  }

  const baseConfig = appStore.current.baseConfig ?? {}
  const { __READONLY__, EDITABLE, FILE_TYPES } = props.config
  const disabled = __READONLY__ || !EDITABLE
  return (
    <Upload
      multiple
      disabled={disabled}
      accept={FILE_TYPES}
      fileList={files.map(item => ({
        uid: item.FileId,
        name: item.FileName,
        status: 'done',
        url: `${baseConfig.FileDomain || getImgBase()}${item.FilePath}`
      }))}
      beforeUpload={beforeUpload}
      onRemove={handleRemove}
    >
      <Button loading={loading} icon={<UploadOutlined />} disabled={disabled}>选择文件</Button>
    </Upload>
  )
}

export default FileUpload